import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PressList, PressListContact, Comment, ApiResponse, PressAsset } from '@pf/shared';
import { apiGet, apiPost, apiPut, apiDelete, apiUpload } from '../lib/api';

export interface CreatePressListBody {
  name: string;
  description?: string;
  tags?: string[];
  role?: string;
  sendingEmail?: string;
  parentId?: string;
  clientId?: string | null;
}

export type UpdatePressListBody = Partial<CreatePressListBody>;

export interface AddContactToListBody {
  contactId: string;
  position?: number;
  selectedEmail?: string;
  notes?: string;
}

export interface CommentWithUser extends Comment {
  userName: string;
}

interface PressListWithContacts extends PressList {
  contacts: PressListContact[];
}

const PRESS_LISTS_KEY = 'press-lists';

export function usePressLists(options?: {
  includeArchived?: boolean;
  q?: string;
  tags?: string;
  clientId?: string;
}) {
  return useQuery({
    queryKey: [PRESS_LISTS_KEY, { includeArchived: options?.includeArchived, q: options?.q, tags: options?.tags, clientId: options?.clientId }],
    queryFn: () =>
      apiGet<{ pressLists: PressList[] }>('/press-lists', {
        includeArchived: options?.includeArchived ? 'true' : undefined,
        q: options?.q || undefined,
        tags: options?.tags || undefined,
        clientId: options?.clientId || undefined,
      }),
  });
}

export function usePressList(id: string | undefined) {
  return useQuery({
    queryKey: [PRESS_LISTS_KEY, id],
    queryFn: () =>
      apiGet<{ pressList: PressListWithContacts; contacts: PressListContact[] }>(
        `/press-lists/${id}`,
      ),
    enabled: !!id,
  });
}

export function useCreatePressList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePressListBody) =>
      apiPost<ApiResponse<PressList>>('/press-lists', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useUpdatePressList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdatePressListBody }) =>
      apiPut<ApiResponse<PressList>>(`/press-lists/${id}`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY, variables.id] });
    },
  });
}

export function useDeletePressList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/press-lists/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useArchivePressList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPut<{ pressList: PressList }>(`/press-lists/${id}/archive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useUnarchivePressList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPut<{ pressList: PressList }>(`/press-lists/${id}/unarchive`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useDuplicatePressList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<{ pressList: PressList }>(`/press-lists/${id}/duplicate`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useAddContactToList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, body }: { listId: string; body: AddContactToListBody }) =>
      apiPost<ApiResponse<PressListContact>>(`/press-lists/${listId}/contacts`, body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY, variables.listId] });
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useBatchAddContactsToList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      contactIds,
    }: {
      listId: string;
      contactIds: string[];
    }) =>
      apiPost<{ added: number; skipped: number }>(
        `/press-lists/${listId}/contacts/batch`,
        { contactIds },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY, variables.listId] });
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useReorderContactsInList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      contactIds,
    }: {
      listId: string;
      contactIds: string[];
    }) =>
      apiPut<{ reordered: number }>(
        `/press-lists/${listId}/contacts/reorder`,
        { contactIds },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRESS_LISTS_KEY, variables.listId],
      });
    },
  });
}

export function useRemoveContactFromList() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, contactId }: { listId: string; contactId: string }) =>
      apiDelete(`/press-lists/${listId}/contacts/${contactId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY, variables.listId] });
      queryClient.invalidateQueries({ queryKey: [PRESS_LISTS_KEY] });
    },
  });
}

export function useUpdateContactSelectedEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      contactId,
      selectedEmail,
    }: {
      listId: string;
      contactId: string;
      selectedEmail: string | null;
    }) =>
      apiPut<{ listContact: PressListContact }>(
        `/press-lists/${listId}/contacts/${contactId}/selected-email`,
        { selectedEmail },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRESS_LISTS_KEY, variables.listId],
      });
    },
  });
}

export function usePressListComments(listId: string | undefined) {
  return useQuery({
    queryKey: [PRESS_LISTS_KEY, listId, 'comments'],
    queryFn: () =>
      apiGet<{ comments: CommentWithUser[] }>(`/press-lists/${listId}/comments`),
    enabled: !!listId,
  });
}

export function useAddPressListComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, text }: { listId: string; text: string }) =>
      apiPost<{ comment: CommentWithUser }>(`/press-lists/${listId}/comments`, {
        text,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRESS_LISTS_KEY, variables.listId, 'comments'],
      });
    },
  });
}

export function useDeletePressListComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      listId,
      commentId,
    }: {
      listId: string;
      commentId: string;
    }) => apiDelete(`/press-lists/${listId}/comments/${commentId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRESS_LISTS_KEY, variables.listId, 'comments'],
      });
    },
  });
}

export function usePressListAssets(listId: string | undefined) {
  return useQuery({
    queryKey: [PRESS_LISTS_KEY, listId, 'assets'],
    queryFn: () => apiGet<{ assets: PressAsset[] }>(`/press-lists/${listId}/assets`),
    enabled: !!listId,
  });
}

export function useUploadPressAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, file }: { listId: string; file: File }) =>
      apiUpload<{ asset: PressAsset }>(`/press-lists/${listId}/assets`, file),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRESS_LISTS_KEY, variables.listId, 'assets'],
      });
    },
  });
}

export function useDeletePressAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ listId, assetId }: { listId: string; assetId: string }) =>
      apiDelete(`/press-lists/${listId}/assets/${assetId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [PRESS_LISTS_KEY, variables.listId, 'assets'],
      });
    },
  });
}

export interface ReporterSuggestion {
  id: string;
  name: string;
  title: string | null;
  imageUrl: string | null;
  beats: string[];
  relevanceScore: number;
  primaryPublication: { id: string; name: string | null; faviconUrl: string | null } | null;
}

export function usePressListSuggestions(listId: string | undefined) {
  return useQuery({
    queryKey: [PRESS_LISTS_KEY, listId, 'suggestions'],
    queryFn: () =>
      apiGet<{ suggestions: ReporterSuggestion[]; message?: string }>(
        `/press-lists/${listId}/suggestions`,
      ),
    enabled: !!listId,
    staleTime: 5 * 60 * 1000,
  });
}
